import * as btc from '@scure/btc-signer';
import { HDKey } from '@scure/bip32';
import * as bip39 from '@scure/bip39';
import { wordlist as englishWordlist } from '@scure/bip39/wordlists/english';
import { secp256k1 } from '@noble/curves/secp256k1';
import { hex } from '@scure/base';

type NetworkName = 'mainnet' | 'testnet';

type UTXO = {
  txid: string; // hex
  vout: number;
  value: number; // sats
  status?: { confirmed?: boolean; block_height?: number };
};

type FeeEstimates = Record<string, number>;

type BuildAndSignParams = {
  mnemonic: string;
  toAddress?: string;
  amountSats?: number;
  outputs?: ({ address: string; amountSats: number } | { scriptHex: string; amountSats: number })[];
  changeAddress?: string;
  feeRateSatPerVb?: number;
  utxos: UTXO[];
  network?: NetworkName;
  // Optional: allow spending from non-default derivation.
  account?: number; // BIP84 account (default 0)
  index?: number; // BIP84 external index (default 0)
};

async function sha256Bytes(message: string): Promise<Uint8Array> {
  const enc = new TextEncoder();
  const digest = await globalThis.crypto.subtle.digest('SHA-256', enc.encode(String(message)));
  return new Uint8Array(digest);
}

function generateSecp256k1KeyPair(): { privateKeyHex: string; publicKeyHex: string } {
  const priv = secp256k1.utils.randomPrivateKey();
  const pub = secp256k1.getPublicKey(priv, true);
  return { privateKeyHex: hex.encode(priv), publicKeyHex: hex.encode(pub) };
}

async function signMessageSha256(message: string, privateKeyHex: string): Promise<string> {
  const hash = await sha256Bytes(String(message));
  const sig: any = (secp256k1 as any).sign(hash, hex.decode(String(privateKeyHex)));
  const sigBytes: Uint8Array = sig?.toCompactRawBytes ? sig.toCompactRawBytes() : sig;
  return hex.encode(sigBytes);
}

function getNetwork(network?: NetworkName) {
  return network === 'testnet' ? btc.TEST_NETWORK : btc.NETWORK;
}

function getBip84Path(network?: NetworkName, account = 0, index = 0) {
  // BIP84: m/84'/{coin_type}'/{account}'/0/{index}
  // coin_type: 0 mainnet, 1 testnet
  const coinType = network === 'testnet' ? 1 : 0;
  return `m/84'/${coinType}'/${account}'/0/${index}`;
}

function getSeedFromMnemonic(mnemonic: string): Uint8Array {
  if (!bip39.validateMnemonic(mnemonic.trim(), englishWordlist)) {
    throw new Error('Invalid mnemonic');
  }
  return bip39.mnemonicToSeedSync(mnemonic.trim());
}

function derivePrivKeyBytesFromMnemonic(mnemonic: string, network?: NetworkName, account = 0, index = 0): Uint8Array {
  const seed = getSeedFromMnemonic(mnemonic);
  const root = HDKey.fromMasterSeed(seed);
  const path = getBip84Path(network, account, index);
  const child = root.derive(path);
  if (!child.privateKey) throw new Error('Failed to derive private key');
  return child.privateKey;
}

function getP2WPKHAddressFromMnemonic(mnemonic: string, network?: NetworkName, account = 0, index = 0): string {
  const priv = derivePrivKeyBytesFromMnemonic(mnemonic, network, account, index);
  const pub = secp256k1.getPublicKey(priv, true);
  const pay = btc.p2wpkh(pub, getNetwork(network));
  if (!pay.address) throw new Error('Failed to derive address');
  return pay.address;
}

function getPublicKeyFromMnemonic(mnemonic: string, network?: NetworkName, account = 0, index = 0): string {
  const priv = derivePrivKeyBytesFromMnemonic(mnemonic, network, account, index);
  const pub = secp256k1.getPublicKey(priv, true);
  return hex.encode(pub);
}

function getPrivateKeyFromMnemonic(mnemonic: string, network?: NetworkName, account = 0, index = 0): string {
  const priv = derivePrivKeyBytesFromMnemonic(mnemonic, network, account, index);
  return hex.encode(priv);
}

// Derive a separate messaging keypair from mnemonic (NOT the BTC spending key)
// Uses domain separation: SHA256("Autho:Messaging:v1" || seed) to ensure independence
async function deriveMessagingKeyPair(mnemonic: string): Promise<{ publicKeyHex: string; privateKeyHex: string }> {
  const seed = getSeedFromMnemonic(mnemonic);
  // Domain separation: hash with a unique prefix so this key can never be confused with BTC key
  const domainPrefix = new TextEncoder().encode('Autho:Messaging:v1:');
  const combined = new Uint8Array(domainPrefix.length + seed.length);
  combined.set(domainPrefix, 0);
  combined.set(seed, domainPrefix.length);
  
  // SHA-256 to get 32 bytes for the messaging private key
  const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', combined);
  const messagingPrivateKey = new Uint8Array(hashBuffer);
  
  // Derive public key using secp256k1 (compatible with nacl.box after conversion)
  const messagingPublicKey = secp256k1.getPublicKey(messagingPrivateKey, true);
  
  return {
    publicKeyHex: hex.encode(messagingPublicKey),
    privateKeyHex: hex.encode(messagingPrivateKey),
  };
}

// Synchronous version for environments where async isn't convenient
function deriveMessagingKeyPairSync(mnemonic: string): { publicKeyHex: string; privateKeyHex: string } {
  const seed = getSeedFromMnemonic(mnemonic);
  // Domain separation
  const domainPrefix = new TextEncoder().encode('Autho:Messaging:v1:');
  const combined = new Uint8Array(domainPrefix.length + seed.length);
  combined.set(domainPrefix, 0);
  combined.set(seed, domainPrefix.length);
  
  // Use a simple SHA-256 implementation for sync (already have sha256Bytes async, need sync)
  // For now, use the seed directly with domain separation via HKDF-like construction
  // Actually, let's use the fact that we can do sync SHA-256 via a fallback
  const messagingPrivateKey = sha256Sync(combined);
  const messagingPublicKey = secp256k1.getPublicKey(messagingPrivateKey, true);
  
  return {
    publicKeyHex: hex.encode(messagingPublicKey),
    privateKeyHex: hex.encode(messagingPrivateKey),
  };
}

// Simple synchronous SHA-256 for browser use
function sha256Sync(bytes: Uint8Array): Uint8Array {
  const K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];
  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));

  const bitLen = bytes.length * 8;
  const withOne = bytes.length + 1;
  const padLen = (withOne % 64 <= 56) ? (56 - (withOne % 64)) : (56 + (64 - (withOne % 64)));
  const totalLen = withOne + padLen + 8;
  const msg = new Uint8Array(totalLen);
  msg.set(bytes, 0);
  msg[bytes.length] = 0x80;
  const view = new DataView(msg.buffer);
  view.setUint32(totalLen - 8, Math.floor(bitLen / 2 ** 32), false);
  view.setUint32(totalLen - 4, bitLen >>> 0, false);

  let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a;
  let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;

  const w = new Uint32Array(64);
  for (let i = 0; i < msg.length; i += 64) {
    for (let t = 0; t < 16; t++) w[t] = view.getUint32(i + t * 4, false);
    for (let t = 16; t < 64; t++) {
      const s0 = (rotr(w[t - 15], 7) ^ rotr(w[t - 15], 18) ^ (w[t - 15] >>> 3)) >>> 0;
      const s1 = (rotr(w[t - 2], 17) ^ rotr(w[t - 2], 19) ^ (w[t - 2] >>> 10)) >>> 0;
      w[t] = (w[t - 16] + s0 + w[t - 7] + s1) >>> 0;
    }

    let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;
    for (let t = 0; t < 64; t++) {
      const S1 = (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) >>> 0;
      const ch = ((e & f) ^ (~e & g)) >>> 0;
      const temp1 = (h + S1 + ch + K[t] + w[t]) >>> 0;
      const S0 = (rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) >>> 0;
      const maj = ((a & b) ^ (a & c) ^ (b & c)) >>> 0;
      const temp2 = (S0 + maj) >>> 0;
      h = g; g = f; f = e; e = (d + temp1) >>> 0;
      d = c; c = b; b = a; a = (temp1 + temp2) >>> 0;
    }
    h0 = (h0 + a) >>> 0; h1 = (h1 + b) >>> 0; h2 = (h2 + c) >>> 0; h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0; h5 = (h5 + f) >>> 0; h6 = (h6 + g) >>> 0; h7 = (h7 + h) >>> 0;
  }

  const out = new Uint8Array(32);
  const outView = new DataView(out.buffer);
  outView.setUint32(0, h0, false); outView.setUint32(4, h1, false);
  outView.setUint32(8, h2, false); outView.setUint32(12, h3, false);
  outView.setUint32(16, h4, false); outView.setUint32(20, h5, false);
  outView.setUint32(24, h6, false); outView.setUint32(28, h7, false);
  return out;
}

function generateMnemonic24(): string {
  return bip39.generateMnemonic(englishWordlist, 256);
}

function normalizeUtxosForTx(mnemonic: string, utxos: UTXO[], network?: NetworkName, account = 0, index = 0) {
  const priv = derivePrivKeyBytesFromMnemonic(mnemonic, network, account, index);
  const pub = secp256k1.getPublicKey(priv, true);
  const spend = btc.p2wpkh(pub, getNetwork(network));

  return utxos.map((u) => ({
    ...spend,
    txid: hex.decode(u.txid),
    index: u.vout,
    witnessUtxo: {
      script: spend.script,
      amount: BigInt(u.value),
    },
  }));
}

function pickFeeRate(feeEstimates: FeeEstimates | null | undefined): number {
  // Esplora fee-estimates returns { "1": sats/vbyte, "2": ..., "6": ... }
  // We'll prefer 2-block, then 1-block, then 6.
  const candidates = ['2', '1', '6', '3'];
  for (const k of candidates) {
    const v = feeEstimates?.[k];
    if (typeof v === 'number' && isFinite(v) && v > 0) return v;
  }
  return 2; // conservative fallback
}

function buildAndSignP2WPKH(params: BuildAndSignParams): { txHex: string; txid: string; feeSats: number } {
  const network = params.network || 'mainnet';
  const account = params.account ?? 0;
  const index = params.index ?? 0;

  const outputsInput = Array.isArray(params.outputs) && params.outputs.length > 0 ? params.outputs : null;
  if (!outputsInput) {
    if (!params.toAddress) throw new Error('Missing toAddress');
    if (!Number.isFinite(params.amountSats) || (params.amountSats as number) <= 0) throw new Error('Invalid amountSats');
  }

  const hasScriptOutputs = !!(outputsInput && outputsInput.some((o: any) => o && typeof (o as any).scriptHex === 'string'));

  const changeAddress = params.changeAddress || getP2WPKHAddressFromMnemonic(params.mnemonic, network, account, index);

  const requestedFeeRate = Number(params.feeRateSatPerVb ?? 2);
  const hasRequestedFeeRate = Number.isFinite(requestedFeeRate) && requestedFeeRate > 0;
  const needsFineTuning = hasRequestedFeeRate && Math.abs(requestedFeeRate - Math.round(requestedFeeRate)) > 1e-9;

  // The underlying UTXO selection requires integer feePerByte >= 1.
  // We'll select at ceil(feeRate) sat/vB, then (if needed) adjust the change output to hit the exact fee.
  const selectionFeePerByte = BigInt(Math.max(1, Math.ceil(hasRequestedFeeRate ? requestedFeeRate : 2)));

  const utxo = normalizeUtxosForTx(params.mnemonic, params.utxos || [], network, account, index);
  const outputs = outputsInput
    ? outputsInput.map((o: any) => {
        const amountSats = Number(o?.amountSats || 0);
        if (!Number.isFinite(amountSats) || amountSats < 0) throw new Error('Invalid output amountSats');

        if (typeof o?.scriptHex === 'string') {
          const scriptHex = String(o.scriptHex || '').trim().toLowerCase();
          if (!/^[0-9a-f]*$/.test(scriptHex) || scriptHex.length < 2) {
            throw new Error('Invalid output scriptHex');
          }
          return { script: hex.decode(scriptHex), amount: BigInt(amountSats) };
        }

        const address = String(o?.address || '').trim();
        if (!address) throw new Error('Invalid output address');
        if (!Number.isFinite(amountSats) || amountSats <= 0) throw new Error('Invalid output amountSats');
        return { address, amount: BigInt(amountSats) };
      })
    : [{ address: String(params.toAddress), amount: BigInt(Number(params.amountSats)) }];

  const selected = btc.selectUTXO(utxo as any, outputs as any, 'default', {
    changeAddress,
    feePerByte: selectionFeePerByte,
    alwaysChange: needsFineTuning,
    bip69: true,
    createTx: true,
    allowUnknownOutputs: hasScriptOutputs,
    network: getNetwork(network),
  } as any);

  if (!selected?.tx) {
    throw new Error('Insufficient funds');
  }

  const priv = derivePrivKeyBytesFromMnemonic(params.mnemonic, network, account, index);

  // If user requested a fractional sat/vB (e.g. 0.2/0.5/2.3), adjust the change output so the final fee matches
  // ceil(vsize * feeRate). Fee must still be integer sats.
  if (needsFineTuning) {
    // Compute actual vsize using a signed clone (signature sizes can vary by a byte).
    const tmp = selected.tx.clone();
    tmp.sign(priv);
    tmp.finalize();
    const vsize = tmp.vsize;
    const desiredFeeSats = BigInt(Math.max(0, Math.ceil(requestedFeeRate * vsize)));

    // Find the change output by script (change address)
    const changeScript = btc.OutScript.encode(btc.Address(getNetwork(network)).decode(changeAddress));
    let changeOutIdx = -1;
    for (let i = 0; i < selected.tx.outputsLength; i++) {
      const out = selected.tx.getOutput(i);
      const script = (out as any)?.script;
      if (script && hex.encode(script) === hex.encode(changeScript)) {
        changeOutIdx = i;
        break;
      }
    }

    if (changeOutIdx === -1) {
      throw new Error('Cannot set fractional sat/vB fee: missing change output. Try adding funds or using a higher fee.');
    }

    const currentFeeSats = BigInt(selected.tx.fee);
    if (currentFeeSats !== desiredFeeSats) {
      const changeOut = selected.tx.getOutput(changeOutIdx);
      const currentChange = BigInt((changeOut as any)?.amount || 0n);

      if (currentFeeSats > desiredFeeSats) {
        const delta = currentFeeSats - desiredFeeSats; // reduce fee by increasing change
        const newChange = currentChange + delta;
        selected.tx.updateOutput(changeOutIdx, { ...changeOut, amount: newChange } as any, true);
      } else {
        const delta = desiredFeeSats - currentFeeSats; // increase fee by decreasing change
        const newChange = currentChange - delta;
        if (newChange <= 0n) {
          throw new Error('Cannot set fractional sat/vB fee: not enough change available. Try adding funds or using a higher fee.');
        }
        selected.tx.updateOutput(changeOutIdx, { ...changeOut, amount: newChange } as any, true);
      }
    }
  }

  selected.tx.sign(priv);
  selected.tx.finalize();

  return {
    txHex: selected.tx.hex,
    txid: selected.tx.id,
    feeSats: Number(selected.tx.fee),
  };
}

declare global {
  interface Window {
    AuthoBTC?: any;
  }
}

window.AuthoBTC = {
  version: '1.1.0',
  getAddressFromMnemonic: (mnemonic: string, network?: NetworkName, account?: number, index?: number) =>
    getP2WPKHAddressFromMnemonic(mnemonic, network, account ?? 0, index ?? 0),
  getPublicKeyFromMnemonic: (mnemonic: string, network?: NetworkName, account?: number, index?: number) =>
    getPublicKeyFromMnemonic(mnemonic, network, account ?? 0, index ?? 0),
  getPrivateKeyFromMnemonic: (mnemonic: string, network?: NetworkName, account?: number, index?: number) =>
    getPrivateKeyFromMnemonic(mnemonic, network, account ?? 0, index ?? 0),
  // Messaging keypair (separate from BTC spending key, uses domain separation)
  deriveMessagingKeyPair: (mnemonic: string) => deriveMessagingKeyPair(mnemonic),
  deriveMessagingKeyPairSync: (mnemonic: string) => deriveMessagingKeyPairSync(mnemonic),
  generateMnemonic24: () => generateMnemonic24(),
  buildAndSignP2WPKH: (params: BuildAndSignParams) => buildAndSignP2WPKH(params),
  generateSecp256k1KeyPair: () => generateSecp256k1KeyPair(),
  signMessageSha256: (message: string, privateKeyHex: string) => signMessageSha256(message, privateKeyHex),
};
